import { Injectable, OnDestroy } from '@angular/core';
import { BaseService } from '../base.service';
import { forkJoin, lastValueFrom, Subject, takeWhile } from 'rxjs';
import { AlertTitles, AlertTypes, APIUrls, ApiUrlTexts, AppSide, AppStatesTexts, errorMsgsPopup, MNGUrls, WebhookFieldTypesList, WebHookTasks, WebHookType } from '../constants';
import { PartnerDataModel } from '../account/models/accountModel';
import { AccountService } from '../account/services/account.service';
import { AppDataModel } from '../features/store/models/storeModel';
import { CreateAppDataModel } from '../management/models/managementModel';
import { NgxSpinnerService } from 'ngx-spinner';
import { AlertService } from '../alert/services/alert.service';
import { WebhookDefData } from './models/sharedModel';
import { FormElement, WebhookCusotmFieldsAllowed, WebHookFormElem } from './sharedModel';

@Injectable({
  providedIn: 'root'
})

export class SharedDataService implements OnDestroy {

  metadataSub = new Subject();

  singlePartnerDataSub = new Subject();

  updatePartnerDataSub = new Subject();

  appDocsUploadDataSub = new Subject();

  associatedPartnersLinkSub = new Subject();

  destroySubscription = false;

  webhookDefData: WebhookDefData;

  getWebhookAppDataSub = new Subject();

  getWebhookSameLevelAppDataSub = new Subject();

  createWebhooksDefDataSub = new Subject();

  webHookFormElem: WebHookFormElem;

  webHookType = WebHookType;

  partnerUpdateFormElemConfig: FormElement;

  // settingsMenuDataSub = new Subject();

  constructor(private accountService: AccountService, private baseService: BaseService,
    private spinner: NgxSpinnerService, private alertService: AlertService,
  ) {
    this.init();
  }

  init() {
    this.webhookDefData = {
      app_id: '',
      custom_fields: '',
      custom_fields_refactored: [],
      payload_fields: null,
      vendor_api_endpoint: '',
      vendor_api_secret: '',
      workflow_def_uuid: '',
      workflow_name: '',
      workflow_task: '',
      workflow_type: '',
      webhook_url: '',
      customer_name: '',
      partner_api_endpoint: '',
      partner_api_secret: '',
      partner_id: '',
      user_id: '',
    }

    this.webHookFormElem = {
      columClass: 'col-lg-4',
      fields: {
        workflow_name: {
          readOnly: false,
          visible: true,
          mandatory: true
        },
        webhook_url: {
          readOnly: false,
          visible: true,
          mandatory: true
        },
        vendor_api_endpoint: {
          readOnly: false,
          visible: true,
          mandatory: true
        },
        partner_api_endpoint: {
          readOnly: false,
          visible: true,
          mandatory: true
        },
        vendor_api_secret: {
          readOnly: false,
          visible: true,
          mandatory: false
        },
        partner_api_secret: {
          readOnly: false,
          visible: true,
          mandatory: false
        },
        custom_fields: {
          readOnly: false,
          visible: true,
          mandatory: false
        }
      }
    }

    this.partnerUpdateFormElemConfig = {
      columClass: 'col-lg-4',
      fields: {
        partnerUpdateFormFields: {
          partner_name: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          short_name: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          description: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          oauth_client_id: {
            readOnly: true,
            visible: true,
            mandatory: false
          },
          oauth_client_secret: {
            readOnly: false,
            visible: true,
            mandatory: false
          },
          app_access_level: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          api_endpoint: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          portal_url: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          registration_status: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          ns_org_id: {
            readOnly: true,
            visible: true,
            mandatory: false
          },
          restrict_mktplace_access: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          ns_sdk_enabled: {
            readOnly: false,
            visible: true,
            mandatory: false
          },
          ns_sdk_callback_secret: {
            readOnly: false,
            visible: true,
            mandatory: false
          },
          contact_name: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          contact_num: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          contact_email: {
            readOnly: false,
            visible: true,
            mandatory: true
          },
          contact_address: {
            readOnly: false,
            visible: true,
            mandatory: true
          }
        }
      }
    }
  }

  getMetadata() {
    this.getData(MNGUrls.metadata)
      .pipe(takeWhile(() => !this.destroySubscription)).subscribe({
        next: (response: any) => {
          if (response && response.status === 200) {
            this.metadataSub.next(response.body);
          }
        }
      });
  }

  // refactorMetadata(app_types: any) {
  //   return app_types.map((item: string) => {
  //     return item === 'PRODUCTIVITY_OR_COLLABORATION' ? 'PRODUCTIVITY/COLLABORATION' : item;
  //   });
  // }

  // refactorMetadataAppTypeToOriginal(app_type: string) {
  //   if (app_type === 'PRODUCTIVITY/COLLABORATION') {
  //     return 'PRODUCTIVITY_OR_COLLABORATION';
  //   } else {
  //     return app_type;
  //   }
  // }

  getPartner(partner_id: string, fromSide: string) {
    let reqUrl = '';
    if(fromSide === AppSide.manageSide) {
      reqUrl = MNGUrls.partnerBase + partner_id;
    } else if(fromSide === AppSide.storeSide) {
      reqUrl = APIUrls.partnerApps + partner_id;
    }
    this.getData(reqUrl)
      .pipe(takeWhile(() => !this.destroySubscription)).subscribe({
        next: (response: any) => {
          if (response && response.status === 200) {
            this.singlePartnerDataSub.next(response.body);
          }
        },
        error: (err: any) => {
          this.singlePartnerDataSub.next(null);
        }
      });
  }

  updatePartnerDataModel(partnerData: any) {
    return {
      api_endpoint: partnerData.api_endpoint ? partnerData.api_endpoint : '',
      created_datetime_utc: partnerData.created_datetime_utc ? partnerData.created_datetime_utc : '',
      description: partnerData.description ? partnerData.description : '',
      partner_id: partnerData.partner_id ? partnerData.partner_id : '',
      partner_name: partnerData.partner_name ? partnerData.partner_name : '',
      portal_url: partnerData.portal_url ? partnerData.portal_url : '',
      registration_status: partnerData.registration_status ? partnerData.registration_status : '',
      short_name: partnerData.short_name ? partnerData.short_name : '',
      contact_address: partnerData.contact_address ? partnerData.contact_address : '',
      contact_email: partnerData.contact_email ? partnerData.contact_email : '',
      contact_name: partnerData.contact_name ? partnerData.contact_name : '',
      contact_num: partnerData.contact_num ? partnerData.contact_num : '',
      oauth_client_id: partnerData.oauth_client_id ? partnerData.oauth_client_id : '',
      oauth_client_secret: partnerData.oauth_client_secret ? partnerData.oauth_client_secret : '',
      app_access_level: partnerData.app_access_level ? partnerData.app_access_level : '',
      restrict_mktplace_access: partnerData.restrict_mktplace_access ? partnerData.restrict_mktplace_access : false,
      ns_org_id: partnerData.ns_org_id ? partnerData.ns_org_id : '',
      ns_sdk_enabled: partnerData.ns_sdk_enabled ? partnerData.ns_sdk_enabled : false,
      ns_sdk_callback_secret: partnerData.ns_sdk_callback_secret ? partnerData.ns_sdk_callback_secret : ''
    }
  }

  updatePartner(updatePartnerDataModel: any, fromSide: string) {
    let reqUrl = '';
    if(fromSide === AppSide.manageSide) {
      reqUrl = MNGUrls.partnerBaseA + '/' + updatePartnerDataModel.partner_id
    } else if(fromSide === AppSide.storeSide) {
      reqUrl = APIUrls.partnerApps + updatePartnerDataModel.partner_id;
    }
    Object.keys(updatePartnerDataModel).forEach(key => {
      if (updatePartnerDataModel[key] === '') {
        delete updatePartnerDataModel[key];
      }
    });
    const updatePartnerDataModelNew = JSON.parse(JSON.stringify(updatePartnerDataModel));
    updatePartnerDataModelNew.contact_num = (String(updatePartnerDataModelNew.contact_num)).replace(/[-\(\)\.\s\+]/g, '');
    this.putRequest(updatePartnerDataModelNew, reqUrl)
      .pipe(takeWhile(() => !this.destroySubscription)).subscribe({
        next: (response: any) => {
          if (response && response.status === 200) {
            const obj = {
              alertType: 'autoClose',
              icon: AlertTypes.success,
              title: AlertTitles.successTitle
            }
            this.alertService.alertSubject.next(obj);

            this.updatePartnerDataSub.next(obj);
          }
        }
      });
  }

  async getAuditLogs(fromSide: string, urlParams?: any) {
    const user = this.accountService.getUser();
    let reqUrl = '';
    if (fromSide === AppSide.storeSide) {
      reqUrl = APIUrls.partnerApps + user.partner_id + '/' + ApiUrlTexts.auditLogs;
    } else if (fromSide === AppSide.manageSide) {
      reqUrl = MNGUrls.manageAuditLogs;
    }
    try {
      return await lastValueFrom(
        this.getData(reqUrl, urlParams.length ? urlParams : '')
      )
    }
    catch (e) {
      return null;
    }
  }

  async getWfLogs(fromSide: string, urlParams?: any) {
    const user = this.accountService.getUser();
    let reqUrl = '';
    if (fromSide === AppSide.storeSide) {

    } else if (fromSide === AppSide.manageSide) {
      reqUrl = MNGUrls.manageWfLogs;
    }
    try {
      return await lastValueFrom(
        this.getData(reqUrl, urlParams.length ? urlParams : '')
      )
    }
    catch (e) {
      return null;
    }
  }

  async getAppStates(fromSide: string) {
    const user = this.accountService.getUser();
    let reqUrl = '';
    if (fromSide === AppSide.storeSide) {
      reqUrl = APIUrls.partnerApps + user.partner_id + '/customer-app/apps';
    } else if (fromSide === AppSide.manageSide) {
      reqUrl = MNGUrls.appStates;
    }
    try {
      return await lastValueFrom(
        this.getData(reqUrl)
      )
    }
    catch (e) {
      return null;
    }
  }

  async getApps(fromSide: string, url: string, urlParams?: any) {
    const user = this.accountService.getUser();
    let reqUrl = url;
    if (fromSide === AppSide.storeSide) {

    } else if (fromSide === AppSide.manageSide) {
      
    }
    try {
      return await lastValueFrom(
        this.getData(reqUrl, urlParams.length ? urlParams : '')
      )
    }
    catch (e) {
      return null;
    }
  }

  // Persists a single reordered promotional rank. `payload` is the dragged row
  // as { app_id, promo_rank }; it is sent as the PUT body.
  async updatePromoRanks(payload: any) {
    const reqUrl = MNGUrls.appBaseWithToken + '/' + payload.app_id;
    try {
      return await lastValueFrom(
        this.putRequest(payload, reqUrl)
      )
    }
    catch (e) {
      return e;
    }
  }

  async updateStateForPartnerApps(appStateData: AppDataModel) {
    let updated_enable_state = '';
    if (appStateData.enable_state === AppStatesTexts.requested) {
      updated_enable_state = AppStatesTexts.processing
    } else if (appStateData.enable_state === AppStatesTexts.processing) {
      updated_enable_state = AppStatesTexts.readytoPublish
    } else if (appStateData.enable_state === AppStatesTexts.readytoPublish) {
      updated_enable_state = AppStatesTexts.available
    }
    const reqObj = {
      "enable_state": updated_enable_state
    }
    const reqUrl = MNGUrls.partnerBase + appStateData.partner_id + '/app/' + appStateData.app_id + '/state';
    try {
      return await lastValueFrom(
        this.patchRequest(reqObj, reqUrl)
      )
    }
    catch (e) {
      return null;
    }
  }

  async updateStateForCustomerApp(appStateData: any, stateToUpdate: string) {
    const reqObj = {
      "enable_state": stateToUpdate
    }
    const reqUrl = APIUrls.partnerApps + appStateData.partner_id + '/customer/' + appStateData.customer_name +
      '/app/' + appStateData.app_id + '/state';
    try {
      return await lastValueFrom(
        this.patchRequest(reqObj, reqUrl)
      )
    }
    catch (e) {
      return null;
    }
  }

  async updateStateForUserApp(appStateData: any, stateToUpdate: string, user: string) {
    const reqObj = {
      "enable_state": stateToUpdate
    }
    const reqUrl = APIUrls.partnerApps + appStateData.partner_id + '/customer/' + appStateData.customer_name +
      '/user/' + user + '/app/' + appStateData.app_id + '/state';
    try {
      return await lastValueFrom(
        this.patchRequest(reqObj, reqUrl)
      )
    }
    catch (e) {
      return null;
    }
  }

  operateTechFileUpload(createAppDataModel: CreateAppDataModel, fromSide: string, partner_id?: any) {
    const newFilesArr: any = [];
    if (createAppDataModel.technicalFiles.length) {
      createAppDataModel.technicalFiles.forEach(file => {
        if (!file.document_id && file.file_name && file.file_type) {
          newFilesArr.push({ file_name: file.file_name, file_type: (file.file_type).toUpperCase() });
        }
      })
    }

    if (createAppDataModel.technicalFilesToDelete.length) {
      this.deleteExistingTechFiles(createAppDataModel, newFilesArr, fromSide, partner_id);
    } else {
      this.requestUploadTechFilesUrls(createAppDataModel, newFilesArr, fromSide, partner_id);
    }
  }

  deleteExistingTechFiles(createAppDataModel: CreateAppDataModel, newFilesArr: Array<any>, fromSide: string, partner_id: string) {
    let filesToDeleteForkjoinArr: any = [];
    createAppDataModel.technicalFilesToDelete.forEach((file: any) => {
      let req;
      if (fromSide === AppSide.storeSide) {
        req = this.deleteRequestForkJoinWithToken(APIUrls.generalApps + createAppDataModel.app_id + '/partner/'
          + partner_id + '/app-document/' + file.document_id);
      } else if (fromSide === AppSide.manageSide) {
        req = this.deleteRequestForkJoinWithToken(MNGUrls.appDocument + file.document_id);
      }
      filesToDeleteForkjoinArr.push(req);
    });

    if (filesToDeleteForkjoinArr.length) {
      this.spinner.show();
      forkJoin(filesToDeleteForkjoinArr)
        .pipe(takeWhile(() => !this.destroySubscription)).subscribe((forkResponse: any) => {
          if (forkResponse && forkResponse.length) {
            this.spinner.hide();
            // this.deleteAppDocsDataSub.next(true);
            this.requestUploadTechFilesUrls(createAppDataModel, newFilesArr, fromSide, partner_id);
          }
        }, (error) => {
          console.log('error at service', error);
          this.spinner.hide();
          const obj = {
            alertType: 'openSwal',
            icon: AlertTypes.error,
            text: errorMsgsPopup.deleteFailed
          }
          this.alertService.alertSubject.next(obj);
        });
    }
  }

  requestUploadTechFilesUrls(createAppDataModel: CreateAppDataModel, newFilesArr: Array<any>, fromSide: string, partner_id: string) {
    if (newFilesArr && newFilesArr.length) {
      let reqUrl = '';
      if (fromSide === AppSide.storeSide) {
        reqUrl = APIUrls.generalApps + createAppDataModel.app_id + '/partner/'
          + partner_id + '/app-documents';
      } else if (fromSide === AppSide.manageSide) {
        reqUrl = MNGUrls.vendorAppBase + '/' + createAppDataModel.app_id + '/app-documents';
      }

      this.postRequest(newFilesArr, reqUrl)
        .pipe(takeWhile(() => !this.destroySubscription)).subscribe({
          next: (response: any) => {
            if (response && response.status === 200) {
              this.uploadTechFiles(createAppDataModel, response.body);
            }
          }
        });
    } else {
      this.appDocsUploadDataSub.next(true);
    }
  }

  uploadTechFiles(createAppDataModel: CreateAppDataModel, uploadData: any) {
    this.spinner.show();
    const combinedArr = createAppDataModel.technicalFiles.map((fileA: any) => {
      const fileB = uploadData.find((file: any) => ((file.file_name === fileA.file_name) && ((file.file_type).toLowerCase() === (fileA.file_type).toLowerCase())));
      return { ...fileA, ...fileB };
    });
    // console.log(combinedArr);
    let forkJoinArr: any = [];
    if (combinedArr && combinedArr.length) {
      combinedArr.forEach((file: any) => {
        if (file.upload_status === 'PENDING') {
          let fields = file?.upload_info?.fields;
          // fields.file = file.file;
          const formData = new FormData();
          const blob = new Blob([file.file]);
          // formData.append("success_action_redirect", fields["success_action_redirect"]);
          // formData.append('key', fields.key);
          // formData.append('AWSAccessKeyId', fields.AWSAccessKeyId);
          // formData.append("x-amz-security-token", fields["x-amz-security-token"]);
          // formData.append('policy', fields.policy);
          // formData.append('signature', fields.signature);
          if (fields && Object.keys(fields).length) {
            for (const [key, value] of Object.entries(fields)) {
              // console.log(`${key}: ${value}`);
              formData.append(`${key}`, `${value}`);
            }
            formData.append('file', blob);
            const req = this.postRequestForkJoinWithFormDataSkipIntercept(formData, file.upload_info.url);
            forkJoinArr.push(req);
          }
        }
      });
    }

    if (forkJoinArr.length) {
      forkJoin(forkJoinArr)
        .pipe(takeWhile(() => !this.destroySubscription)).subscribe((forkResponse: any) => {
          if (forkResponse && forkResponse.length) {
            this.spinner.hide();
            // this.getAppDocs(createAppDataModel.app_id);
            this.appDocsUploadDataSub.next(true);
          }
        }, (error) => {
          console.log('error at service', error);
          this.spinner.hide();
          const obj = {
            alertType: 'openSwal',
            icon: AlertTypes.error,
            text: errorMsgsPopup.uploadFailed
          }
          this.alertService.alertSubject.next(obj);
        });
    }
  }

  blurModalACtiveElem() {
    const elem = document.activeElement as HTMLElement;
    elem.blur();
  }

  getWebhooks(app_id: any, fromSide?: string, partner_id?: string, customer_name?: string, user_id?: string) {
    let getWebhookstUrl = '';
    const partnerLevel = partner_id ? ('/partner/' + partner_id) : '';
    const customerLevel = customer_name ? ('/customer/' + customer_name) : '';
    const userLevel = user_id ? ('/user/' + user_id) : '';
    if (fromSide && fromSide === AppSide.storeSide) {
      getWebhookstUrl = APIUrls.generalApps + app_id + (partnerLevel ? partnerLevel : '') + (customerLevel ? customerLevel : '') + (userLevel ? userLevel : '') + '/workflow-definitions';
    } else {
      getWebhookstUrl = MNGUrls.workflowDefinitionAppBase + '/' + app_id + (partnerLevel ? partnerLevel : '') + '/workflow-definitions';
    }

    this.getWebhooksAction(getWebhookstUrl);
  }

  getWebhooksAction(url: string) {
    this.getDataNoParams(url)
      .pipe(takeWhile(() => !this.destroySubscription)).subscribe({
        next: (response: any) => {
          if (response && response.status === 200) {
            this.getWebhookAppDataSub.next(response.body);
          }
        }
      });
  }

  getWebhooksSameLevel(app_id: any, fromSide?: string, partner_id?: string, customer_name?: string, user_id?: string) {
    let getWebhookstUrl = '';
    const partnerLevel = partner_id ? ('/partner/' + partner_id) : '';
    const customerLevel = customer_name ? ('/customer/' + customer_name) : '';
    const userLevel = user_id ? ('/user/' + user_id) : '';
    if (fromSide && fromSide === AppSide.storeSide) {
      getWebhookstUrl = APIUrls.generalApps + app_id + (partnerLevel ? partnerLevel : '') + (customerLevel ? customerLevel : '') + (userLevel ? userLevel : '') + '/workflow-definitions';
    } else {
      getWebhookstUrl = MNGUrls.workflowDefinitionAppBase + '/' + app_id + '/workflow-definitions';
    }

    this.getDataNoParams(getWebhookstUrl)
      .pipe(takeWhile(() => !this.destroySubscription)).subscribe({
        next: (response: any) => {
          if (response && response.status === 200) {
            this.getWebhookSameLevelAppDataSub.next(response.body);
          }
        }
      });
  }

  addWebhooksManageForApp(webHookFormsData: any) {
    if (webHookFormsData) {
      const partnerWebhookDefData = webHookFormsData.partnerWebhookDefData;
      const customerWebhookDefData = webHookFormsData.customerWebhookDefData;
      const userWebhookDefData = webHookFormsData.userWebhookDefData;
      const partner_wh_uuid = (partnerWebhookDefData && partnerWebhookDefData.workflow_def_uuid) ? partnerWebhookDefData.workflow_def_uuid : null;
      const customer_wh_uuid = (customerWebhookDefData && customerWebhookDefData.workflow_def_uuid) ? customerWebhookDefData.workflow_def_uuid : null;
      const user_wh_uuid = (userWebhookDefData && userWebhookDefData.workflow_def_uuid) ? userWebhookDefData.workflow_def_uuid : null;

      const requestObj: any = {
        app_id: webHookFormsData.app_id,
        is_PARTNER_APP_ENABLE: webHookFormsData.is_PARTNER_APP_ENABLE,
        is_CUSTOMER_APP_ENABLE: webHookFormsData.is_CUSTOMER_APP_ENABLE,
        is_USER_APP_ENABLE: webHookFormsData.is_USER_APP_ENABLE,
        partnerWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.partnerWebhookDefData, partner_wh_uuid, false, webHookFormsData.app_id),
        customerWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.customerWebhookDefData, customer_wh_uuid, false, webHookFormsData.app_id),
        userWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.userWebhookDefData, user_wh_uuid, false, webHookFormsData.app_id)
      }

      const forkJoinObj: any = {};

      let requestUrl = MNGUrls.workflowDefinitionAppBase + '/' + webHookFormsData.app_id + '/workflow-definition';

      if (requestObj.is_PARTNER_APP_ENABLE) {
        let response;
        // const workflow_def_uuid = requestObj.partnerWebhookDefData.workflow_def_uuid;
        if (partner_wh_uuid) {
          response = this.putDataForkJoinWithToken(requestObj.partnerWebhookDefData, requestUrl + '/' + partner_wh_uuid);
        } else {
          response = this.postDataForkJoinWithToken(requestObj.partnerWebhookDefData, requestUrl);
        }
        forkJoinObj.partnerAppEnableWh = response;
      }

      if (requestObj.is_CUSTOMER_APP_ENABLE) {
        let response;
        // const workflow_def_uuid = requestObj.customerWebhookDefData.workflow_def_uuid;
        if (customer_wh_uuid) {
          response = this.putDataForkJoinWithToken(requestObj.customerWebhookDefData, requestUrl + '/' + customer_wh_uuid);
        } else {
          response = this.postDataForkJoinWithToken(requestObj.customerWebhookDefData, requestUrl);
        }
        forkJoinObj.customerAppEnableWh = response;
      }

      if (requestObj.is_USER_APP_ENABLE) {
        let response;
        // const workflow_def_uuid = requestObj.userWebhookDefData.workflow_def_uuid;
        if (user_wh_uuid) {
          response = this.putDataForkJoinWithToken(requestObj.userWebhookDefData, requestUrl + '/' + user_wh_uuid);
        } else {
          response = this.postDataForkJoinWithToken(requestObj.userWebhookDefData, requestUrl);
        }
        forkJoinObj.userAppEnableWh = response;
      }
      this.createWebhooksDefAction(forkJoinObj);
    }
  }

  updateWebhooksManageForAppPerPartner(webHookFormsData: any) {
    if (webHookFormsData) {
      const customerWebhookDefData = webHookFormsData.customerWebhookDefData;
      const userWebhookDefData = webHookFormsData.userWebhookDefData;
      const customer_wh_uuid = (customerWebhookDefData && customerWebhookDefData.workflow_def_uuid) ? customerWebhookDefData.workflow_def_uuid : null;
      const user_wh_uuid = (userWebhookDefData && userWebhookDefData.workflow_def_uuid) ? userWebhookDefData.workflow_def_uuid : null;

      const requestObj = {
        app_id: webHookFormsData.app_id,
        is_CUSTOMER_APP_ENABLE: (webHookFormsData.customerWebhookDefData && webHookFormsData.customerWebhookDefData.workflow_task === WebHookTasks.CUSTOMER_APP_ENABLE),
        is_USER_APP_ENABLE: (webHookFormsData.userWebhookDefData && webHookFormsData.userWebhookDefData.workflow_task === WebHookTasks.USER_APP_ENABLE),
        customerWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.customerWebhookDefData, customer_wh_uuid, false, webHookFormsData.app_id, webHookFormsData.partner_id),
        userWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.userWebhookDefData, user_wh_uuid, false, webHookFormsData.app_id, webHookFormsData.partner_id)
      }

      const forkJoinObj: any = {};

      const requestUrl = MNGUrls.workflowDefinitionAppBase + '/' + webHookFormsData.app_id + '/partner/' + webHookFormsData.partner_id + '/workflow-definition';


      if (requestObj.is_CUSTOMER_APP_ENABLE) {
        let response;
        response = this.putDataForkJoinWithToken(requestObj.customerWebhookDefData, requestUrl + '/' + customer_wh_uuid);
        forkJoinObj.customerAppEnableWh = response;
      }

      if (requestObj.is_USER_APP_ENABLE) {
        let response;
        response = this.putDataForkJoinWithToken(requestObj.userWebhookDefData, requestUrl + '/' + user_wh_uuid);
        forkJoinObj.userAppEnableWh = response;
      }
      this.createWebhooksDefAction(forkJoinObj);
    }
  }

  createWebhooksManageForPartner(webHookFormsData: any) {
    if (webHookFormsData) {
      const requestObj = {
        app_id: webHookFormsData.app_id,
        is_PARTNER_APP_ENABLE: (webHookFormsData.partnerWebhookDefData && webHookFormsData.partnerWebhookDefData.workflow_task === WebHookTasks.PARTNER_APP_ENABLE),
        is_CUSTOMER_APP_ENABLE: (webHookFormsData.customerWebhookDefData && webHookFormsData.customerWebhookDefData.workflow_task === WebHookTasks.CUSTOMER_APP_ENABLE),
        is_USER_APP_ENABLE: (webHookFormsData.userWebhookDefData && webHookFormsData.userWebhookDefData.workflow_task === WebHookTasks.USER_APP_ENABLE),
        partnerWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.partnerWebhookDefData, null, true, webHookFormsData.app_id, webHookFormsData.partner_id),
        customerWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.customerWebhookDefData, null, false, webHookFormsData.app_id, webHookFormsData.partner_id),
        userWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.userWebhookDefData, null, false, webHookFormsData.app_id, webHookFormsData.partner_id)
      }

      const forkJoinObj: any = {};

      const requestUrl = MNGUrls.workflowDefinitionAppBase + '/' + webHookFormsData.app_id + '/partner/' + webHookFormsData.partner_id + '/workflow-definition';

      if (requestObj.is_PARTNER_APP_ENABLE) {
        const response = this.postDataForkJoinWithToken(requestObj.partnerWebhookDefData, requestUrl);
        forkJoinObj.partnerAppEnableWh = response;
      }
      if (requestObj.is_CUSTOMER_APP_ENABLE) {
        const response = this.postDataForkJoinWithToken(requestObj.customerWebhookDefData, requestUrl);
        forkJoinObj.customerAppEnableWh = response;
      }
      if (requestObj.is_USER_APP_ENABLE) {
        const response = this.postDataForkJoinWithToken(requestObj.userWebhookDefData, requestUrl);
        forkJoinObj.userAppEnableWh = response;
      }
      // console.log('final object', forkJoinObj);
      this.createWebhooksDefAction(forkJoinObj);
    }
  }

  createWebhooksDefStoreForCustomer(webHookFormsData: any) {
    if (webHookFormsData) {
      const requestObj = {
        app_id: webHookFormsData.app_id,
        is_CUSTOMER_APP_ENABLE: (webHookFormsData.customerWebhookDefData && webHookFormsData.customerWebhookDefData.workflow_task === WebHookTasks.CUSTOMER_APP_ENABLE),
        is_USER_APP_ENABLE: (webHookFormsData.userWebhookDefData && webHookFormsData.userWebhookDefData.workflow_task === WebHookTasks.USER_APP_ENABLE),
        customerWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.customerWebhookDefData, null, true, webHookFormsData.app_id, webHookFormsData.partner_id, webHookFormsData.customer_name),
        userWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.userWebhookDefData, null, false, webHookFormsData.app_id, webHookFormsData.partner_id, webHookFormsData.customer_name)
      }

      const forkJoinObj: any = {};

      const requestUrl = APIUrls.generalApps + webHookFormsData.app_id + '/partner/' + webHookFormsData.partner_id + '/customer/' + webHookFormsData.customer_name + '/workflow-definition';

      if (requestObj.is_CUSTOMER_APP_ENABLE) {
        const response = this.postDataForkJoinWithToken(requestObj.customerWebhookDefData, requestUrl);
        forkJoinObj.customerAppEnableWh = response;
      }
      if (requestObj.is_USER_APP_ENABLE) {
        const response = this.postDataForkJoinWithToken(requestObj.userWebhookDefData, requestUrl);
        forkJoinObj.userAppEnableWh = response;
      }
      // console.log('final object', forkJoinObj);
      this.createWebhooksDefAction(forkJoinObj);
    }
  }

  createWebhooksDefStoreForUser(webHookFormsData: any) {
    if (webHookFormsData) {
      const requestObj = {
        app_id: webHookFormsData.app_id,
        is_USER_APP_ENABLE: (webHookFormsData.userWebhookDefData && webHookFormsData.userWebhookDefData.workflow_task === WebHookTasks.USER_APP_ENABLE),
        userWebhookDefData: this.getAppWebHookCreateDataObj(webHookFormsData.userWebhookDefData, null, true, webHookFormsData.app_id, webHookFormsData.partner_id, webHookFormsData.customer_name, webHookFormsData.user_id)
      }

      const forkJoinObj: any = {};

      const requestUrl = APIUrls.generalApps + webHookFormsData.app_id + '/partner/' + webHookFormsData.partner_id + '/customer/' + webHookFormsData.customer_name + '/user/' + webHookFormsData.user_id + '/workflow-definition';

      if (requestObj.is_USER_APP_ENABLE) {
        const response = this.postDataForkJoinWithToken(requestObj.userWebhookDefData, requestUrl);
        forkJoinObj.userAppEnableWh = response;
      }
      // console.log('final object', forkJoinObj);
      this.createWebhooksDefAction(forkJoinObj);
    }
  }

  createWebhooksDefAction(forkJoinObj: any) {
    this.spinner.show();

    forkJoin(forkJoinObj)
      .pipe(takeWhile(() => !this.destroySubscription)).subscribe((forkResponse: any) => {
        this.spinner.hide();
        if (forkResponse && Object.keys(forkResponse).length) {
          this.createWebhooksDefDataSub.next(forkResponse);
        }
      }, (error) => {
        this.spinner.hide();
      });
  }

  getRefactoredCustomFields(custom_fields_refactored: any) {
    if (custom_fields_refactored && custom_fields_refactored.length) {
      const newCustomFields: any = [];
      custom_fields_refactored.forEach((item: any) => {
        // if (item.fieldLabel && item.value) {
        //   // console.log(item);
        //   const tempObj: any = {}
        //   tempObj[item.fieldLabel] = item.value;
        //   newCustomFields.push(tempObj)
        // }
        if (item.fieldLabel) {
          // console.log(item);
          const tempObj: any = {}
          // tempObj[item.fieldLabel] = item.value ? item.value : '';
          if (item.fieldName) {
            tempObj[item.fieldName] = item.value ? item.value : '';
          } else {
            tempObj[item.fieldLabel] = item.value ? item.value : '';
          }
          newCustomFields.push(tempObj)
        }
      });
      return newCustomFields;
    } else {
      return [];
    }
  }

  getAppWebHookCreateDataObj(webhookDefData: WebhookDefData, workflow_def_uuid: any, isCustomFieldsRefactored: boolean, app_id: string, partner_id?: string, customer_name?: string, user_id?: string) {
    if (webhookDefData) {
      const custom_fields = (isCustomFieldsRefactored && webhookDefData.custom_fields_refactored) ? this.getRefactoredCustomFields(webhookDefData.custom_fields_refactored) : webhookDefData.custom_fields ? this.getVerifiedJson(webhookDefData.custom_fields) : [];
      return {
        workflow_def_uuid: workflow_def_uuid ? workflow_def_uuid : null,
        workflow_name: webhookDefData.workflow_name,
        app_id: app_id,
        partner_id: partner_id ? partner_id : null,
        customer_name: customer_name ? customer_name : null,
        user_id: user_id ? user_id : null,
        workflow_task: webhookDefData.workflow_task,
        workflow_type: this.webHookType.EXTERNAL_WEBHOOK,
        webhook_url: webhookDefData.webhook_url,
        vendor_api_endpoint: webhookDefData.vendor_api_endpoint,
        payload_fields: webhookDefData.payload_fields ? webhookDefData.payload_fields : null,
        custom_fields: custom_fields,
        partner_api_endpoint: webhookDefData.partner_api_endpoint ? webhookDefData.partner_api_endpoint : null,
        vendor_api_secret: webhookDefData.vendor_api_secret ? webhookDefData.vendor_api_secret : null,
        partner_api_secret: webhookDefData.partner_api_secret ? webhookDefData.partner_api_secret : null
      }
    } else {
      return null;
    }
  }

  getVerifiedJson(custom_fields: any) {
    if (custom_fields) {
      if (typeof custom_fields === 'string') {
        return JSON.parse(custom_fields);
      } else if (typeof custom_fields !== 'string') {
        return custom_fields;
      }
    } else {
      return '';
    }
  }

  async executeWebhooksAction(fromSide: string, isCustomerUser: boolean, app_id: string, partner_id: string, customer_name: any, new_wf_uuid: string, domainToExecuteWf: any, userToExecuteWf: any) {
    let requestUrl = '';
    if (fromSide && fromSide === AppSide.manageSide) {
      requestUrl = MNGUrls.appBaseWithToken + '/' + app_id + '/partner/' + partner_id + '/workflow-definition/' + new_wf_uuid + '/workflow-execution';
    } else {
      requestUrl = APIUrls.generalApps + app_id + '/partner/' + partner_id;
      if (isCustomerUser) {
        requestUrl = requestUrl + '/customer/' + customer_name + '/user/' + userToExecuteWf + '/workflow-definition/' + new_wf_uuid + '/workflow-execution';
      } else {
        requestUrl = requestUrl + '/customer/' + domainToExecuteWf + '/workflow-definition/' + new_wf_uuid + '/workflow-execution';
      }
    }
    try {
      return await lastValueFrom(
        this.postRequest('', requestUrl)
      )
    }
    catch (e) {
      return null;
    }
  }

  verifyRefactoredCustomFields(custom_fields_refactored?: Array<WebhookCusotmFieldsAllowed>) {
    const tempArr: any = [];
    if (custom_fields_refactored && custom_fields_refactored.length) {
      custom_fields_refactored.forEach((field) => {
        if (!field.fieldType) {
          field.fieldType = 'text';
        }
        if (field.fieldType === 'dropdown') {
          if (!field.value) { // handled update case
            field.value = field?.fieldOptions?.values[0]?.option ? field?.fieldOptions?.values[0]?.option : '';
          }
        }
        const isAllowedTtype = WebhookFieldTypesList.find(type => field.fieldType === type);
        if (isAllowedTtype && (field.fieldLabel || field.fieldName)) {
          tempArr.push(field);
        }
      });
      return tempArr;
    } else {
      return tempArr;
    }

  }

  getDataNoParams(reqUrl: string) {
    return this.baseService.getData(reqUrl);
  }

  getData(reqUrl: string, urlParams?: any) {
    let urlData = '';
    if (urlParams && urlParams.length) {
      urlParams.forEach((val: any, index: any) => {
        if (index === 0) {
          urlData += '?' + val.paramLabel + '=' + val.paramValue
        } else {
          urlData += '&' + val.paramLabel + '=' + val.paramValue;
        }

      });
    }
    return this.baseService.getData(reqUrl + (urlData ? urlData : ''));
  }

  patchRequest(reqObj: any, reqUrl: string, urlParams?: any) {
    let urlData = '';
    if (urlParams && urlParams.length) {
      urlParams.forEach((val: any) => {
        urlData += '&' + val.paramLabel + '=' + val.paramValue
      });
    }
    return this.baseService.patchData(reqObj, reqUrl + (urlData ? urlData : ''));
  }

  deleteRequestForkJoinWithToken(reqUrl: string) {
    return this.baseService.deleteRequestForkJoinWithToken(reqUrl);
  }

  postRequest(reqObj: any, reqUrl: string, urlParams?: any) {
    let urlData = '';
    if (urlParams && urlParams.length) {
      urlParams.forEach((val: any) => {
        urlData += '&' + val.paramLabel + '=' + val.paramValue
      });
    }
    return this.baseService.postData(reqObj, reqUrl + (urlData ? urlData : ''));
  }

  putRequest(reqObj: any, reqUrl: string, urlParams?: any) {
    let urlData = '';
    if (urlParams && urlParams.length) {
      urlParams.forEach((val: any) => {
        urlData += '&' + val.paramLabel + '=' + val.paramValue
      });
    }
    return this.baseService.putData(reqObj, reqUrl + (urlData ? urlData : ''));
  }

  postDataForkJoinWithToken(reqObj: any, reqUrl: string) {
    return this.baseService.postDataForkJoinWithToken(reqObj, reqUrl);
  }

  putDataForkJoinWithToken(reqObj: any, reqUrl: string) {
    return this.baseService.putDataForkJoinWithToken(reqObj, reqUrl);
  }

  postRequestForkJoinWithFormData(contentType: any, reqObj: any, reqUrl: string, urlParams?: any) {
    let urlData = '';
    if (urlParams && urlParams.length) {
      urlParams.forEach((val: any) => {
        urlData += '&' + val.paramLabel + '=' + val.paramValue
      });
    }
    return this.baseService.postRequestForkJoinWithFormData(contentType ? contentType : '', reqObj, reqUrl);
  }

  postRequestForkJoinWithFormDataSkipIntercept(reqObj: any, reqUrl: string) {
    return this.baseService.postRequestForkJoinWithFormDataSkipIntercept(reqObj, reqUrl);
  }

  getDataForkJoinWithToken(reqUrl: string) {
    return this.baseService.getDataForkJoinWithToken(reqUrl);
  }

  async awaitGetRequest(reqUrl: string, urlParams?: any) {
    try {
      return await lastValueFrom(
        this.getData(reqUrl, urlParams)
      )
    }
    catch (e) {
      return null;
    }
  }

  async awaitPostRequest(reqObj: any, reqUrl: string,) {
    try {
      return await lastValueFrom(
        this.postRequest(reqObj, reqUrl)
      )
    }
    catch (e) {
      return null;
    }
  }

  ngOnDestroy(): void {
    this.destroySubscription = true;
  }

}